<template>
  <div class="dashboard">
    <div class="main-content">
      <navbar-dashboard name="Mis Envios"></navbar-dashboard>

      <b-modal id="share-guide-modal" ref="shareGuideModal" title="Compartir Guía de Envío" size="lg" centered
        hide-footer v-model="isModalVisible">
        <div class="p-2">
          <div class=" d-flex justify-content-between align-items-center">
            <span><strong>Guía:</strong> {{ selectedShipment.number_guide }}</span>
            <span class="badge badge-primary">{{ selectedShipment.selected_service ?
              selectedShipment.selected_service.carrier : '' }}</span>
          </div>

          <div class="form-group">
            <label for="messageBody" class="font-weight-bold"> Mensaje personalizado:</label>
            <textarea id="messageBody" v-model="form.messageBody" class="form-control" rows="2"
              placeholder="Escribe una descripción para el correo..."></textarea>
          </div>

          <span class="font-weight-bold"> Enviar a los siguientes correos:</span>
          <div class="table-responsive">
            <table class="table table-hover table-bordered mb-0">
              <thead class="thead-light">
                <tr>
                  <th>Email del Destinatario</th>
                  <th style="width: 100px;" class="text-center">Acción</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(email, index) in form.emails" :key="index">
                  <td class="p-1">
                    <label :for="`email-${index}`" class="sr-only">Email del Destinatario</label>
                    <input :id="`email-${index}`" v-model="form.emails[index]" type="email"
                      class="form-control border-0" placeholder="ejemplo@correo.com" />
                  </td>
                  <td class="text-center">
                    <button v-if="form.emails.length > 1" class="btn btn-sm btn-outline-danger"
                      @click="removeEmail(index)">
                      <i class="bi bi-trash-fill"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <button class="btn btn-sm btn-link mt-2 p-0" @click="addEmail"
            v-b-tooltip.hover.top="'Agrega otro destinatario'">
            <i class="bi bi-plus-circle-fill"></i> Agregar otro destinatario
          </button>

          <div class="mt-4 d-flex justify-content-end">
            <button class="btn btn-light mr-2" @click="isModalVisible = false"
              v-b-tooltip.hover.top="'Cancelar y cerrar modal'">
              Cancelar
            </button>
            <button class="btn btn-primary px-4" :disabled="isSending" @click="sendEmails"
              v-b-tooltip.hover.top="'Enviar la guía a los correos especificados'">
              <span v-if="isSending" class="spinner-border spinner-border-sm mr-1"></span>
              <i v-else class="bi bi-send mr-1"></i>
              {{ isSending ? 'Enviando...' : 'Enviar Ahora' }}
            </button>
          </div>
        </div>
      </b-modal>
      <!-- Alert -->
      <b-alert :show="dismissCountDown" dismissible variant="success" @dismissed="dismissCountDown = 0"
        @dismiss-count-down="countDownChanged">
        {{ message }}
      </b-alert>
      <div class="ew-view">
        <page-header title="Gestión de envíos" subtitle="Consulta, rastrea y comparte tus envíos.">
          <button @click="exportExcel" class="btn btn-outline-secondary"
            v-b-tooltip.hover.top="'Exportar la lista de envíos filtrados a un archivo Excel'">
            <i class="bi bi-file-earmark-spreadsheet mr-1"></i> Exportar a Excel
          </button>
          <router-link class="btn btn-primary" to="/dashboard/shipment/new">
            <i class="bi bi-plus-lg mr-1"></i> Nuevo envío
          </router-link>
        </page-header>

        <!-- Filtros -->
        <div class="card ew-filters mb-3">
          <div class="form-row">
            <div class="col-6 col-md-4 col-xl-2 form-group">
              <label for="startDate">Fecha inicio</label>
              <input id="startDate" type="date" v-model="filters.startDate" class="form-control" @change="applyFilters">
            </div>
            <div class="col-6 col-md-4 col-xl-2 form-group">
              <label for="endDate">Fecha fin</label>
              <input id="endDate" type="date" v-model="filters.endDate" class="form-control" @change="applyFilters">
            </div>
            <div class="col-6 col-md-4 col-xl-2 form-group">
              <label for="carrier">Paquetería</label>
              <select id="carrier" v-model="filters.carrier" class="custom-select" @change="applyFilters">
                <option value="">Todas</option>
                <option v-for="c in carriers" :key="c" :value="c">{{ c | carrierName }}</option>
              </select>
            </div>
            <div class="col-6 col-md-4 col-xl-2 form-group">
              <label for="shipmentType">Tipo de envío</label>
              <select id="shipmentType" v-model="filters.shipmentType" class="custom-select" @change="applyFilters">
                <option value="">Todos</option>
                <option v-for="t in shipmentTypes" :key="t" :value="t">{{ shipmentTypeLabels[t] || t }}</option>
              </select>
            </div>
            <div class="col-6 col-md-4 col-xl-2 form-group">
              <label for="filter-has-guide">Estado de la guía</label>
              <select id="filter-has-guide" v-model="filters.hasGuide" class="custom-select" @change="applyFilters">
                <option value="">Todos</option>
                <option :value="true">Con guía generada</option>
                <option :value="false">Sin guía (borradores)</option>
              </select>
            </div>
            <div class="col-6 col-md-4 col-xl-2 form-group">
              <label for="filter-number-guide">N° de guía</label>
              <input id="filter-number-guide" type="search" v-model="filters.number_guide" class="form-control"
                @input="applyFilters" placeholder="Buscar guía">
            </div>
          </div>
        </div>

        <!-- Tabla -->
        <div class="ew-table-scroll">
          <table class="table table-hover mb-0">
            <thead>
              <tr>
                <th>Origen</th>
                <th>Destino</th>
                <th>Paquetería</th>
                <th>Servicio</th>
                <th>N° de guía</th>
                <th class="ew-num">Días</th>
                <th v-if="!hasRole(['user'])" class="ew-num">Total</th>
                <th class="text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <template v-if="isLoading">
                <tr v-for="n in 6" :key="'sk-' + n">
                  <td v-for="c in 8" :key="c"><span class="ew-skeleton"></span></td>
                </tr>
              </template>
              <tr v-for="(s, i) in shipments" v-else :key="i">
                <td>
                  <div class="ew-party" :title="formatAddress(s.origin_direction)">
                    <span class="ew-party-name">{{ s.origin_direction && s.origin_direction.full_name | dash }}</span>
                    <span class="ew-party-place">{{ formatPlace(s.origin_direction) }}</span>
                  </div>
                </td>
                <td>
                  <div class="ew-party" :title="formatAddress(s.destination_direction)">
                    <span class="ew-party-name">{{ s.destination_direction && s.destination_direction.full_name | dash }}</span>
                    <span class="ew-party-place">{{ formatPlace(s.destination_direction) }}</span>
                  </div>
                </td>
                <td>{{ s.selected_service.carrier | carrierName }}</td>
                <td class="text-nowrap">{{ s.selected_service.carrier_service_name | serviceName }}</td>
                <td>
                  <div v-for="(num, index) in splitData(s.number_guide)" :key="'num-' + index">
                    <a v-if="num" :href="getTrackingUrl(s.selected_service.carrier, num)" target="_blank"
                      rel="noopener" class="ew-guide-link" v-b-tooltip.hover.top="'Ver estatus de seguimiento'">{{ num }}</a>
                    <span v-else class="text-muted">Sin guía</span>
                  </div>
                </td>
                <td class="ew-num">{{ s.selected_service.total_transit_days | dash }}</td>
                <td v-if="!hasRole(['user'])" class="ew-num font-weight-600">
                  {{ (s.total_guia || s.selected_service.total_amount) | money }}
                </td>
                <td class="text-center text-nowrap">
                  <template v-for="(path, index) in splitData(s.guide)">
                    <a v-if="path" :key="'pdf-' + index" :href="formatSingleGuidePath(path)" target="_blank" rel="noopener"
                      class="btn btn-sm btn-outline-secondary ew-icon-btn mr-1"
                      v-b-tooltip.hover.top="splitData(s.guide).length > 1 ? 'Descargar guía ' + (index + 1) : 'Descargar guía (PDF)'"
                      aria-label="Descargar guía en PDF">
                      <i class="bi bi-file-earmark-pdf"></i>
                    </a>
                  </template>
                  <span v-if="!splitData(s.guide).some(Boolean)" class="btn btn-sm btn-outline-secondary ew-icon-btn mr-1 disabled"
                    v-b-tooltip.hover.top="'Guía aún no generada'" aria-label="Guía aún no generada">
                    <i class="bi bi-file-earmark-pdf"></i>
                  </span>
                  <b-dropdown size="sm" variant="outline-secondary" right no-caret boundary="window"
                    toggle-class="ew-icon-btn" :popper-opts="{ positionFixed: true }" class="ew-row-menu">
                    <template #button-content>
                      <i class="bi bi-three-dots" aria-hidden="true"></i><span class="sr-only">Más acciones</span>
                    </template>
                    <b-dropdown-item-button :disabled="!s.guide" @click="openShareModal(s)">
                      <i class="bi bi-envelope mr-2"></i> Compartir por correo
                    </b-dropdown-item-button>
                    <b-dropdown-item-button @click="gotoShipmentDetails(s._id)">
                      <i class="bi bi-receipt mr-2"></i> Ver detalle del envío
                    </b-dropdown-item-button>
                  </b-dropdown>
                </td>
              </tr>
              <tr v-if="!isLoading && shipments.length === 0">
                <td colspan="8" class="p-0">
                  <empty-state icon="bi bi-box-seam" title="No hay envíos con estos filtros"
                    text="Ajusta los filtros o crea un nuevo envío.">
                    <router-link class="btn btn-primary btn-sm" to="/dashboard/shipment/new">
                      <i class="bi bi-plus-lg mr-1"></i> Nuevo envío
                    </router-link>
                  </empty-state>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <table-pagination :page="pagination.page" :total-pages="pagination.totalPages" :per-page="Number(pagination.limit)"
          :per-page-options="[20, 25, 50, 100]" @change="changePage" @per-page="setPerPage" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.ew-guide-link {
  font-variant-numeric: tabular-nums;
}

.font-weight-600 {
  font-weight: 600;
}
</style>


<script>
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import Navbar from './Header/Navbar'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import axios from 'axios'
import commons from '../commons'
import { roleMixin } from '@/mixins/roleMixin';

export default {
  name: 'Dashboard',
  mixins: [roleMixin],
  data() {
    return {
      message: "",
      dismissSecs: 5,
      dismissCountDown: 0,
      searchKeyword: '',  // Texto de búsqueda
      selected: [],
      availableCarriers: [],
      // Variables para el modal
      isModalVisible: false,
      selectedShipment: {},
      isSending: false,
      form: {
        messageBody: 'Hola, te comparto los datos de envío de tu paquete.',
        emails: ['']
      },
      //Filtrado
      shipments: [],
      isLoading: true,
      shipmentTypeLabels: { document: 'Sobre', box: 'Caja', tarima: 'Tarima' },
      carriers: [],
      shipmentTypes: ['document', 'box', 'tarima'],
      pagination: {
        page: 1,
        limit: 20,
        totalPages: 0
      },
      filters: {
        startDate: '',
        endDate: '',
        shipmentType: '',
        carrier: '',
        client: "",
        hasGuide: true,
        number_guide: ''
      },
    }
  },
  created() {
    this.setNewShipmentEcommerce(null);
    this.getUser()
    this.fetchData()
    this.$on('shipmentCreated', () => {
      this.fetchData()
    })
    setTimeout(() => {
      this.fetchData()
    }, 1500)

  },
  mounted() {
    this.fetchCarrierFromShipment();
  },
  beforeDestroy() {
  },
  methods: {
    fetchCarrierFromShipment() {
      axios.get(`${commons.apiURL}/api/v1/carrier/getCarrierFromShipments`)
        .then(res => {
          this.carriers = res.data.map(item => item.cve);
        })
        .catch(err => console.error(err));
    },
    openShareModal(shipment) {
      if (!shipment.guide) {
        return
      }
      this.selectedShipment = shipment;
      this.isModalVisible = true;
      // Resetear emails al abrir
      this.form.emails = [''];
    },
    closeModal() {
      this.isModalVisible = false;
    },
    addEmail() {
      this.form.emails.push('');
    },
    removeEmail(index) {
      this.form.emails.splice(index, 1);
    },
    async sendEmails() {
      const activeEmails = this.form.emails.filter(e => e.includes('@'));
      if (activeEmails.length === 0) return alert("Ingresa al menos un correo válido");

      this.isSending = true;
      try {
        axios
          .post(`${commons.apiURL}/api/v1/send-guide-emails`, {
            shipmentId: this.selectedShipment._id,
            emails: activeEmails,
            messageBody: this.form.messageBody
          })
          .then(response => {
            if (response.status == 200) {
              this.closeModal();
            }
          })
          .catch(error => {
            console.error(error);
          })
      } catch (e) {
        alert("Error al enviar correos.");
        console.error(e);
      } finally {
        this.isSending = false;
      }
    },

    // "Ciudad, EDO · C.P. 00000"
    formatPlace(dir) {
      if (!dir) return '';
      const city = [dir.city, dir.state_short].filter(Boolean).join(', ');
      return dir.postal_code ? `${city} · C.P. ${dir.postal_code}` : city;
    },
    setPerPage(limit) {
      this.pagination.limit = limit;
      this.applyFilters();
    },
    // Formatea dirección completa
    formatAddress(dir) {
      if (!dir) return '';
      return `${dir.full_name}, ${dir.address}, ${dir.colony}, ${dir.city}, ${dir.state_short || ''}, C.P. ${dir.postal_code}`;
    },
    // Devuelve la URL de seguimiento según el carrier
    getTrackingUrl(carrier, number) {
      const c = carrier.toLowerCase();
      if (!number) return null;

      switch (c) {
        case 'dhl': return this.trackingDhl(number);
        case 'fedex': return this.trackingFedex(number);
        case 'estafeta': return this.trackingEstafeta(number);
        case 'paquetexpress': return this.trackingPaquete(number);
        case 'ptx': return `https://www.ptxpaqueteria.com/rastreo/${number}`;
        default: return null;
      }
    }, exportExcel() {
      const currentUserRole = localStorage.getItem("ew-user-role") || null;

      const data = this.shipments.map(s => {
        // Obtenemos el último estado del tracking de forma segura
        const lastTracking = s.tracking && s.tracking.length > 0
          ? (s.tracking[s.tracking.length - 1].status || s.tracking[s.tracking.length - 1].estado)
          : 'Pendiente de seguimiento';

        return {
          "Guía": s.number_guide,
          "Tipo de envío": s.shipment_type,
          "Estatus": lastTracking, // Se usa el valor dinámico del tracking
          "Transportista": s.selected_service.carrier,
          "Servicio": s.selected_service.carrier_service_name,
          ...((currentUserRole !== 'user') ? {
            "Costo": `${s.selected_service.total_amount} ${s.selected_service.currency}`,
          } : {}),
          "Moneda": s.selected_service.currency,
          "Contenido": s.description_of_the_content,
          "Seguro": s.package_insurance ? 'Sí' : 'No',
          "Origen": `${s.origin_direction.full_name}, ${s.origin_direction.address}, ${s.origin_direction.city}, ${s.origin_direction.state_short}, ${s.origin_direction.postal_code}`,
          "Destino": `${s.destination_direction.full_name}, ${s.destination_direction.address}, ${s.destination_direction.city}, ${s.destination_direction.state_short}, ${s.destination_direction.postal_code}`
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Envios");
      XLSX.writeFile(workbook, "reporte_envios.xlsx");
    },
    exportPDF() {
      const doc = new jsPDF();
      const columns = [
        "Guía", "Tipo de envío", "Estatus", "Transportista", "Servicio", "Costo", "Moneda", "Contenido", "Seguro", "Origen", "Destino"
      ];

      const rows = this.shipments.map(s => {
        const lastTracking = s.tracking && s.tracking.length > 0
          ? (s.tracking[s.tracking.length - 1].status || s.tracking[s.tracking.length - 1].estado)
          : 'Pendiente de seguimiento';

        return [
          s.number_guide,
          s.shipment_type,
          lastTracking,
          s.selected_service.carrier,
          s.selected_service.carrier_service_name,
          s.selected_service.total_amount,
          s.selected_service.currency,
          s.description_of_the_content,
          s.package_insurance ? 'Sí' : 'No',
          `${s.origin_direction.full_name}, ${s.origin_direction.address}, ${s.origin_direction.city}`,
          `${s.destination_direction.full_name}, ${s.destination_direction.address}, ${s.destination_direction.city}`
        ];
      });

      doc.autoTable({
        head: [columns],
        body: rows,
        startY: 20,
        styles: { fontSize: 8 }
      });

      doc.save('reporte_envios.pdf');
    },
    formatSingleGuidePath(path) {
      if (!path) return null;
      const currentOrigin = window.location.origin;
      const apiPath = '/api/public/';
      const prefixRegex = /^(\.\/)?src\/public\//;

      if (prefixRegex.test(path)) {
        return currentOrigin + apiPath + path.replace(prefixRegex, '');
      }
      return path;
    },
    getTrackingUrl(carrier, number) {
      if (!number || !carrier) return null;
      // Asumimos que tus métodos actuales (trackingDhl, etc.) ya manejan strings
      const c = carrier.toLowerCase();
      switch (c) {
        case 'dhl': return this.trackingDhl(number);
        case 'fedex': return this.trackingFedex(number);
        case 'estafeta': return this.trackingEstafeta(number);
        case 'paquetexpress': return this.trackingPaquete(number);
        case 'ptx': return `https://www.ptxpaqueteria.com/rastreo/${number}`;
        default: return null;
      }
    },
    splitData(data) {
      if (!data) return [];
      return typeof data === 'string' ? data.split(',').map(s => s.trim()) : [data];
    },
    formatGuidePath(path) {
      if (!path) return null;

      // 1. Origen actual (ej: https://app.dominio.com)
      const currentOrigin = window.location.origin;

      // 2. Ruta de la API
      const apiPath = '/api/public/';

      /**
       * 3. Regex para limpiar el prefijo:
       * ^(\.\/)? -> Busca opcionalmente un punto y una diagonal al inicio
       * src\/public\/ -> Busca la cadena exacta src/public/
       */
      const prefixRegex = /^(\.\/)?src\/public\//;

      if (prefixRegex.test(path)) {
        // 4. Reemplazamos el prefijo detectado por un string vacío para obtener solo el nombre del archivo
        const filename = path.replace(prefixRegex, '');

        // 5. Retornamos la URL construida
        const finalPath = currentOrigin + apiPath + filename;
        console.log("Formatted path:", finalPath);
        return finalPath;
      }

      // Si ya es una URL completa (http...) no hacemos nada
      return path;
    },
    highlightMatches(text) {
      if (!text) return '';
      const matchExists = text.toLowerCase().includes(this.searchKeyword.toLowerCase());
      if (!matchExists) return text;

      const re = new RegExp(this.searchKeyword, 'ig');
      return text.replace(re, matchedText => `<strong>${matchedText}</strong>`);
    },
    localCapitalize(string) {
      return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
    },
    numberGuide(number, status, i) {
      if (number !== null) {
        return number
      } else {
        return "Sin numero de guia"
      }
    },
    trackingPaquete(guia) {
      const url = `https://www.paquetexpress.com.mx/rastreo/${guia}`
      return url
    },
    trackingDhl(guia) {
      const url = `https://www.dhl.com/mx-es/home/rastreo.html?tracking-id=${guia}`
      return url
    },
    trackingFedex(guia) {
      const url = `https://www.fedex.com/fedextrack/?trknbr=${guia}`
      return url
    },
    trackingEstafeta(guia) {
      const url = `https://cs.estafeta.com/es/Tracking/searchByGet?wayBill=${guia}&wayBillType=0&isShipmentDetail=False`
      return url
    },
    countDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown
    },
    showAlert() {
      this.dismissCountDown = this.dismissSecs
    },
    gotoShipmentDetails(id) {
      this.$router.push(`/shipments/${id}`)
    },
    async fetchData() {
      const userId = localStorage.getItem('ew-user-id');
      const params = {
        page: this.pagination.page,
        limit: this.pagination.limit,
        startDate: this.filters.startDate,
        endDate: this.filters.endDate,
        shipmentType: this.filters.shipmentType,
        carrier: this.filters.carrier,
        number_guide: this.filters.number_guide,
        hasGuide: this.filters.hasGuide
      };

      this.isLoading = this.shipments.length === 0;
      try {
        const response = await axios.get(`${commons.apiURL}/api/v1/shipments/all/${userId}`, { params });

        this.shipments = response.data.docs;
        this.pagination.totalPages = response.data.totalPages;

        this.setGuideStatus([]);
        const status = await this.getStatusGuide({ data: this.shipments });
        this.setGuideStatus(status);
      } catch (error) {
        console.error("Error cargando envíos:", error);
      } finally {
        this.isLoading = false;
      }
    },

    changePage(newPage) {
      this.pagination.page = newPage;
      this.fetchData();
    },
    applyFilters() {
      this.pagination.page = 1;
      this.fetchData();
    },
    ...mapMutations(['setShipments']),
    ...mapActions(['getStatusGuide', 'getUser']),
    ...mapMutations(["setGuideStatus", "setPaymentShipments"]),
    ...mapMutations(['setNewShipmentEcommerce'])
  },
  computed: {
    ...mapGetters(['guideStatus', 'user', "paymentShipments", 'marketplace']),
  },
  components: {
    'navbar-dashboard': Navbar
  },
}
</script>



// WEBPACK FOOTER //
// src/components/ShipmentsList.vue